import { createFile, MP4BoxBuffer, DataStream, Endianness, type Movie, type Track, type Sample } from 'mp4box';

/**
 * Decodes MP4/H.264 video frames using mp4box.js (demuxer) + WebCodecs
 * VideoDecoder (hardware decoder). Bypasses <video> element entirely —
 * no user gesture required on iOS WKWebView.
 *
 * Architecture (follows W3C WebCodecs audio-video-player reference):
 *   [Demuxer] → batch decode → [Frame Buffer (3 frames)] → chooseFrame(time) → canvas
 *
 * The decoder pipeline runs independently of the render loop:
 * - fillFrameBuffer() keeps 3 decoded frames buffered ahead
 * - render(time) picks the closest frame from the buffer
 * - Stale frames are closed to free GPU memory
 */

const FRAME_BUFFER_TARGET = 3;
/** Feed enough samples to overcome the hardware decoder's reorder buffer.
 *  Apple's VideoToolbox assumes max reorder depth when VUI is absent from the SPS,
 *  so we need to feed well beyond 3 samples before the first frame is emitted. */
const DECODE_QUEUE_TARGET = 16;

/** Enable verbose video decoder diagnostics (codec config, frame output, queue state) */
const DIAG_VIDEO_DECODER = false;

interface BufferedFrame {
  frame: VideoFrame;
  timeS: number; // timestamp in seconds
}

export class VideoFrameDecoder {
  private mp4 = createFile();
  private decoder: VideoDecoder | null = null;
  private decoderConfig: VideoDecoderConfig | null = null;
  private _ready = false;
  private _duration = 0;
  private _width = 0;
  private _height = 0;
  private closed = false;

  /** All samples from mp4box (extracted once at init) */
  private samples: Sample[] = [];
  /** Index of the next sample to feed to the decoder */
  private nextSampleIdx = 0;
  /** Buffer of decoded frames, sorted by timestamp */
  private frameBuffer: BufferedFrame[] = [];
  /** Whether fillFrameBuffer is currently running */
  private filling = false;
  /** Target time for a deferred render after async backward seek */
  private pendingRenderTime: number | null = null;
  /** The keyframe index used for the current in-flight seek reset (or -1 if none) */
  private seekResetKeyframeIdx = -1;
  /** The keyframe index that the current buffer was decoded from (or -1 if empty) */
  private bufferOriginKeyframeIdx = -1;
  /** AVCC NAL unit length prefix size in bytes (1, 2, or 4; from avcC byte 4) */
  private _nalLengthSize = 4;

  onFrame: ((frame: VideoFrame) => void) | null = null;
  onLog: ((msg: string) => void) | null = null;
  /** Logs buffered before onLog is set (constructor runs before caller can attach onLog) */
  private earlyLogs: string[] = [];
  private log(msg: string) {
    if (this.onLog) this.onLog(msg);
    else this.earlyLogs.push(msg);
  }
  /** Call after setting onLog to flush any logs from the constructor */
  flushEarlyLogs() {
    if (this.onLog) {
      for (const msg of this.earlyLogs) this.onLog(msg);
    }
    this.earlyLogs = [];
  }

  get ready() { return this._ready; }
  get duration() { return this._duration; }
  get width() { return this._width; }
  get height() { return this._height; }

  constructor(data: ArrayBuffer) {
    this.mp4.onReady = (info: Movie) => {
      const track = info.videoTracks[0];
      if (!track) return;

      this._duration = info.duration / info.timescale;
      this._width = track.video?.width ?? 0;
      this._height = track.video?.height ?? 0;

      this.initDecoder(track);
    };

    this.mp4.onSamples = (_trackId: number, _user: unknown, extracted: Sample[]) => {
      for (const s of extracted) this.samples.push(s);
    };

    this.mp4.onError = (_module: string, message: string) => {
      this.log(`mp4box error: ${message}`);
    };

    const buf = MP4BoxBuffer.fromArrayBuffer(data, 0);
    this.mp4.appendBuffer(buf);
    this.mp4.flush();
  }

  private initDecoder(track: Track) {
    let description: Uint8Array | undefined;
    try {
      const trak = this.mp4.getTrackById(track.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entry = (trak as any).mdia.minf.stbl.stsd.entries[0];
      const descBox = entry.avcC || entry.hvcC;
      if (descBox) {
        const stream = new DataStream(undefined, 0, Endianness.BIG_ENDIAN);
        descBox.write(stream);
        const written = stream.getPosition();
        description = new Uint8Array(stream.buffer.slice(8, written) as ArrayBuffer);
        this._nalLengthSize = (description[4] & 0x03) + 1;
      }
    } catch (e) {
      this.log(`failed to extract codec description: ${e}`);
    }

    // WebKit's WebCodecs silently fails to decode H.264 when the codec string
    // has constraint_set_flags=0x00 (e.g. avc1.4d001f from Apple VideoToolbox).
    // Patch the middle byte to 0x40 so WebKit uses a compatible decode path.
    // This only affects decoder selection, not actual bitstream interpretation.
    let codec = track.codec;
    if (codec.startsWith('avc1.') && codec.length >= 11) {
      const middle = codec.substring(7, 9);
      if (middle === '00') {
        codec = codec.substring(0, 7) + '40' + codec.substring(9);
        if (DIAG_VIDEO_DECODER) this.log(`patched codec string: ${track.codec} → ${codec}`);
      }
    }

    const config: VideoDecoderConfig = {
      codec,
      codedWidth: track.video?.width,
      codedHeight: track.video?.height,
      description,
      optimizeForLatency: true,
    };

    if (DIAG_VIDEO_DECODER) this.log(`configuring decoder: codec=${config.codec}, ${config.codedWidth}x${config.codedHeight}, description=${description ? description.byteLength + 'B' : 'none'}`);

    this.decoder = new VideoDecoder({
      output: (frame: VideoFrame) => {
        if (DIAG_VIDEO_DECODER) this.log(`output frame: ts=${(frame.timestamp / 1_000_000).toFixed(3)}s, ${frame.displayWidth}x${frame.displayHeight}, bufLen=${this.frameBuffer.length}`);
        if (this.closed) { frame.close(); return; }
        const entry: BufferedFrame = {
          frame,
          timeS: frame.timestamp / 1_000_000,
        };
        // Insert in sorted order by presentation timestamp.
        // Decode order ≠ presentation order when B-frames are present.
        let insertIdx = this.frameBuffer.length;
        while (insertIdx > 0 && this.frameBuffer[insertIdx - 1].timeS > entry.timeS) {
          insertIdx--;
        }
        this.frameBuffer.splice(insertIdx, 0, entry);
        // Keep filling — the decoder produced output so there may be room for more input
        this.fillFrameBuffer();
        // If a backward seek is waiting for frames, render now
        if (this.pendingRenderTime !== null) {
          const t = this.pendingRenderTime;
          this.pendingRenderTime = null;
          this.bufferOriginKeyframeIdx = this.seekResetKeyframeIdx;
          this.seekResetKeyframeIdx = -1;
          this.render(t);
        }
      },
      error: (err: DOMException) => {
        this.log(`decoder error: ${err.name}: ${err.message}`);
      },
    });

    this.decoderConfig = config;
    this.decoder.configure(config);

    // Extract ALL samples from mp4box (metadata + data, no decoding yet)
    this.mp4.setExtractionOptions(track.id);
    this.mp4.start();
    this.mp4.flush();
    this.mp4.stop();

    if (DIAG_VIDEO_DECODER) {
      const syncCount = this.samples.filter(s => s.is_sync).length;
      const withData = this.samples.filter(s => s.data && s.data.byteLength > 0).length;
      this.log(`ready: ${this.samples.length} samples (${syncCount} keyframes, ${withData} with data), ${this._width}x${this._height}, ${this._duration.toFixed(1)}s`);
    }
    this._ready = true;

    // Pre-fill the frame buffer before first render
    this.bufferOriginKeyframeIdx = 0;
    this.fillFrameBuffer();
    if (DIAG_VIDEO_DECODER) {
      this.log(`after initial fill: bufferLen=${this.frameBuffer.length}, nextSampleIdx=${this.nextSampleIdx}, decodeQueueSize=${this.decoder.decodeQueueSize}, state=${this.decoder.state}`);

      // Diagnostic: detect if the decoder never produces output
      const diag = this.decoder;
      setTimeout(() => {
        if (!this.closed && this.frameBuffer.length === 0) {
          this.log(`DIAG: no frames after 500ms! decoder.state=${diag.state}, decodeQueueSize=${diag.decodeQueueSize}, nextSampleIdx=${this.nextSampleIdx}, samples=${this.samples.length}`);
        }
      }, 500);
    }
  }

  /**
   * Keep the frame buffer filled by feeding samples to the decoder.
   * Respects both buffer depth and decoder queue depth to avoid flooding.
   */
  private fillFrameBuffer() {
    if (this.filling || this.closed || !this.decoder) return;
    if (this.decoder.state !== 'configured') return;
    this.filling = true;

    while (
      this.frameBuffer.length < FRAME_BUFFER_TARGET &&
      this.decoder.decodeQueueSize < DECODE_QUEUE_TARGET &&
      this.nextSampleIdx < this.samples.length
    ) {
      const sample = this.samples[this.nextSampleIdx];
      this.nextSampleIdx++;

      if (!sample.data) continue;

      // Strip SEI NAL units (type 6) — Apple's VideoToolbox encoder injects
      // SEI NALs that some WebCodecs implementations choke on.
      const rawBytes = sample.data instanceof Uint8Array
        ? sample.data : new Uint8Array(sample.data);
      const cleaned = this.stripSeiNals(rawBytes);

      const chunk = new EncodedVideoChunk({
        type: sample.is_sync ? 'key' : 'delta',
        timestamp: (sample.cts / sample.timescale) * 1_000_000,
        duration: (sample.duration / sample.timescale) * 1_000_000,
        data: cleaned,
      });

      try {
        this.decoder.decode(chunk);
      } catch {
        this.log(`decode error at sample ${this.nextSampleIdx - 1}`);
        break;
      }
    }

    this.filling = false;
  }

  /**
   * Choose the best frame from the buffer for the given timestamp.
   * Closes and discards frames older than the chosen one.
   * Calls onFrame with the result and refills the buffer.
   */
  render(timeSeconds: number) {
    if (this.closed || this.frameBuffer.length === 0) {
      // No frames yet — try to fill
      this.fillFrameBuffer();
      return;
    }

    const targetUs = timeSeconds * 1_000_000;

    // Find the frame with the smallest |timestamp - target| delta.
    // Frame buffer is sorted by presentation timestamp (ascending).
    let bestIdx = 0;
    let bestDelta = Math.abs(this.frameBuffer[0].frame.timestamp - targetUs);

    for (let i = 1; i < this.frameBuffer.length; i++) {
      const delta = Math.abs(this.frameBuffer[i].frame.timestamp - targetUs);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      } else {
        break; // timestamps are sorted, delta increasing means we passed the best
      }
    }

    // Close and discard all frames BEFORE the chosen one (they're stale)
    for (let i = 0; i < bestIdx; i++) {
      this.frameBuffer[i].frame.close();
    }
    this.frameBuffer.splice(0, bestIdx);

    // Deliver the chosen frame (now at index 0)
    const chosen = this.frameBuffer[0];
    if (chosen && this.onFrame) {
      this.onFrame(chosen.frame);
    }

    // Refill the buffer for upcoming frames
    this.fillFrameBuffer();
  }

  /**
   * Find the nearest keyframe at or before the given time.
   */
  private findKeyframeBefore(targetTime: number): number {
    let keyframeIdx = 0;
    for (let i = 0; i < this.samples.length; i++) {
      const sampleTimeS = this.samples[i].cts / this.samples[i].timescale;
      if (sampleTimeS > targetTime) break;
      if (this.samples[i].is_sync) keyframeIdx = i;
    }
    return keyframeIdx;
  }

  seek(timeSeconds: number) {
    if (!this._ready || !this.decoder || this.closed) return;

    const targetTime = Math.min(timeSeconds, this._duration);

    // Check if the target is within or ahead of the current buffer
    const bufferStart = this.frameBuffer.length > 0 ? this.frameBuffer[0].timeS : -1;
    const bufferEnd = this.frameBuffer.length > 0 ? this.frameBuffer[this.frameBuffer.length - 1].timeS : -1;

    if (bufferStart >= 0 && targetTime >= bufferStart - 0.01) {
      // Target is within or ahead of the buffer — just render
      this.pendingRenderTime = null;
      this.seekResetKeyframeIdx = -1;
      this.render(targetTime);
      return;
    }

    // Backward seek — find the keyframe we'd need to reset to
    const keyframeIdx = this.findKeyframeBefore(targetTime);

    // If a reset is already in-flight from the same keyframe, just update
    // the target time. This prevents the starvation loop where rapid seek()
    // calls keep resetting the decoder before it produces any frames.
    if (this.seekResetKeyframeIdx >= 0 && keyframeIdx === this.seekResetKeyframeIdx) {
      this.pendingRenderTime = targetTime;
      return;
    }

    // If the buffer was already decoded from this keyframe, the current
    // frames are the closest we can get — render from the buffer instead
    // of resetting. This handles the common case where the first decoded
    // frame's PTS is slightly after 0s (e.g. 0.08s) and seeks to 0.00s
    // would otherwise infinitely reset to the same keyframe.
    if (this.frameBuffer.length > 0 && this.bufferOriginKeyframeIdx === keyframeIdx) {
      this.render(targetTime);
      return;
    }

    if (DIAG_VIDEO_DECODER) this.log(`backward seek to ${targetTime.toFixed(2)}s (buffer: ${bufferStart.toFixed(2)}-${bufferEnd.toFixed(2)}s, kf=${keyframeIdx})`);

    // Close all buffered frames
    for (const bf of this.frameBuffer) bf.frame.close();
    this.frameBuffer = [];
    this.bufferOriginKeyframeIdx = -1;

    // Reset decoder
    if (this.decoder.state !== 'closed' && this.decoderConfig) {
      this.decoder.reset();
      this.decoder.configure(this.decoderConfig);
    }

    this.nextSampleIdx = keyframeIdx;
    this.seekResetKeyframeIdx = keyframeIdx;
    this.pendingRenderTime = null;

    // Refill buffer from keyframe, then render
    this.fillFrameBuffer();

    // Try to render immediately if buffer has frames.
    // If empty, decoding is async — store target so the output callback
    // renders when the first frame arrives.
    if (this.frameBuffer.length > 0) {
      this.seekResetKeyframeIdx = -1;
      this.bufferOriginKeyframeIdx = keyframeIdx;
      this.render(targetTime);
    } else {
      this.pendingRenderTime = targetTime;
    }
  }

  /**
   * Strip SEI NAL units (type 6) from AVCC-formatted sample data.
   * Apple's VideoToolbox encoder injects SEI NALs that can cause issues
   * with some WebCodecs implementations.
   */
  private stripSeiNals(data: Uint8Array): ArrayBuffer {
    const ls = this._nalLengthSize;
    const out: Uint8Array[] = [];
    let totalLen = 0;
    let offset = 0;

    while (offset + ls < data.length) {
      let nalLen = 0;
      for (let i = 0; i < ls; i++) {
        nalLen = (nalLen << 8) | data[offset + i];
      }
      if (nalLen <= 0 || offset + ls + nalLen > data.length) break;

      const nalType = data[offset + ls] & 0x1f;
      if (nalType !== 6) {
        const slice = data.slice(offset, offset + ls + nalLen);
        out.push(slice);
        totalLen += slice.byteLength;
      }
      offset += ls + nalLen;
    }

    if (totalLen === data.byteLength) {
      return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
    }

    const result = new Uint8Array(totalLen);
    let pos = 0;
    for (const chunk of out) {
      result.set(chunk, pos);
      pos += chunk.byteLength;
    }
    return result.buffer;
  }

  close() {
    if (this.closed) return;
    this.closed = true;

    for (const bf of this.frameBuffer) bf.frame.close();
    this.frameBuffer = [];

    if (this.decoder && this.decoder.state !== 'closed') {
      try { this.decoder.close(); } catch { /* ok */ }
    }
    this.decoder = null;

    this.mp4.stop();
    this.onFrame = null;
    this.samples = [];
  }
}
